'use client';

import type { ElementNode } from '@payloadcms/richtext-slate';
import classNames from 'classnames';

import { PayloadSlateRenderer } from '@/components/PayloadSlateRenderer/PayloadSlateRenderer';
import type { DataAttributes } from '@/types';
import { Image } from '@/ui/Image/Image';
import { Body } from '@/ui/Section/Body/Body';
import { Section } from '@/ui/Section/Section';

import { Content } from '../../Content/Content';
import type { Cta } from '../Cta';

import { SubscribeForm } from './SubscribeForm';

import styles from './SubscribeCta.module.scss';

interface Props extends DataAttributes {
    id: Cta.Props['id'];
    className?: string;
    title: Cta.Props['title'];
    body: Cta.Props['body'];
    media: Cta.Props['media'];
    buttonLabel: Cta.Props['buttonLabel'];
    showAsHero?: boolean;
    successMessage: Cta.Props['successMessage'];
    primary: Cta.Props['primary'];
}

export function SubscribeCta({
    id,
    className,
    title,
    body,
    media,
    buttonLabel,
    successMessage,
    primary = 'white',
    showAsHero,
    ...attributes
}: Props) {
    const subscribeFormContent = (
        <SubscribeForm
            classes={{
                form: styles.form,
            }}
            buttonLabel={buttonLabel}
            id={id}
            successMessage={successMessage}
            title={title}
            isHeroForm={showAsHero}
        />
    );

    if (showAsHero) {
        return (
            <Content
                isHero
                className={classNames(className, styles.hero, {
                    [styles.withImage]: Boolean(media),
                })}
                customActions={subscribeFormContent}
                title={title}
                body={body}
                hasMedia={Boolean(media)}
                media={{
                    mobile_file: media,
                    desktop_file: media,
                }}
                mediaPosition={media ? 'right' : undefined}
            />
        );
    }

    return (
        <Section
            data-subscribe-form={id}
            {...attributes}
            primary={primary}
            className={classNames(className, styles.container)}
        >
            <div className={styles.wrapper}>
                <h3 className={styles.title}>{title}</h3>
                <Body>
                    <PayloadSlateRenderer
                        data-subscribe-form-body
                        className={styles.description}
                        nodes={body as ElementNode[]}
                        options={{ replaceCurrency: true }}
                    />
                </Body>

                {subscribeFormContent}

                <Image
                    alt="Prezly logo"
                    className={styles.illustration}
                    src="/images/subscribe-prezly-logo.svg"
                />
            </div>
        </Section>
    );
}
